import { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Lock, Truck, Undo2, ChevronUp, ChevronDown, Clock, Loader2 } from 'lucide-react';
import { CHECKOUT } from '@/constants/testIds';
import { resolveImageUrl } from '@/components/ImageUpload';
import PixelTracker from '@/components/PixelTracker';
import { VivaPayForm } from '@/components/VivaPayForm';
import { PayzenPayForm } from '@/components/PayzenPayForm';
import { PromoCodeField, PromoSummaryRow } from '@/components/checkout/PromoCodeField';

const BL = {
  fr: { trust: ['Paiement 100% sécurisé', 'Satisfait ou remboursé', 'Livraison suivie offerte'], summary: 'Résumé de la commande', shipping: 'Livraison', free_ship: 'Livraison gratuite', eta: 'Livraison estimée entre le {a} et le {b}', payment: 'Paiement', secure_conn: 'Connexion sécurisée', express: 'PAIEMENT EXPRESS EN 1 CLIC', countdown_today: "Commandez sous {t} pour une expédition aujourd'hui", countdown_tomorrow: 'Commandez sous {t} pour une expédition demain', or_card: 'OU PAYER PAR CARTE', address: 'Adresse de livraison', ship_method: "Mode d'expédition", pay: 'Valider ma commande', secured_via: 'Paiement sécurisé via', your_order: 'Votre commande', qty: 'Quantité', total: 'Total', to_choose: 'À choisir' },
  en: { trust: ['100% secure payment', 'Money-back guarantee', 'Free tracked shipping'], summary: 'Order summary', shipping: 'Shipping', free_ship: 'Free shipping', eta: 'Estimated delivery between {a} and {b}', payment: 'Payment', secure_conn: 'Secure connection', express: 'EXPRESS 1-CLICK PAYMENT', countdown_today: 'Order within {t} for same-day shipping', countdown_tomorrow: 'Order within {t} to ship tomorrow', or_card: 'OR PAY BY CARD', address: 'Shipping address', ship_method: 'Shipping method', pay: 'Complete my order', secured_via: 'Secure payment via', your_order: 'Your order', qty: 'Quantity', total: 'Total', to_choose: 'To choose' },
  es: { trust: ['Pago 100% seguro', 'Satisfecho o reembolsado', 'Envío con seguimiento gratis'], summary: 'Resumen del pedido', shipping: 'Envío', free_ship: 'Envío gratis', eta: 'Entrega estimada entre el {a} y el {b}', payment: 'Pago', secure_conn: 'Conexión segura', express: 'PAGO EXPRÉS EN 1 CLIC', countdown_today: 'Pide en {t} para envío hoy', countdown_tomorrow: 'Pide en {t} para envío mañana', or_card: 'O PAGAR CON TARJETA', address: 'Dirección de envío', ship_method: 'Método de envío', pay: 'Confirmar mi pedido', secured_via: 'Pago seguro vía', your_order: 'Tu pedido', qty: 'Cantidad', total: 'Total', to_choose: 'A elegir' },
  de: { trust: ['100% sichere Zahlung', 'Geld-zurück-Garantie', 'Kostenloser Versand mit Tracking'], summary: 'Bestellübersicht', shipping: 'Versand', free_ship: 'Kostenloser Versand', eta: 'Lieferung voraussichtlich zwischen {a} und {b}', payment: 'Zahlung', secure_conn: 'Sichere Verbindung', express: 'EXPRESS-ZAHLUNG MIT 1 KLICK', countdown_today: 'Bestellen Sie innerhalb {t} für Versand heute', countdown_tomorrow: 'Bestellen Sie innerhalb {t} für Versand morgen', or_card: 'ODER MIT KARTE ZAHLEN', address: 'Lieferadresse', ship_method: 'Versandart', pay: 'Bestellung bestätigen', secured_via: 'Sichere Zahlung über', your_order: 'Ihre Bestellung', qty: 'Menge', total: 'Gesamt', to_choose: 'Auswählen' },
  it: { trust: ['Pagamento 100% sicuro', 'Soddisfatti o rimborsati', 'Spedizione tracciata gratuita'], summary: "Riepilogo dell'ordine", shipping: 'Spedizione', free_ship: 'Spedizione gratuita', eta: 'Consegna stimata tra il {a} e il {b}', payment: 'Pagamento', secure_conn: 'Connessione sicura', express: 'PAGAMENTO EXPRESS IN 1 CLIC', countdown_today: 'Ordina entro {t} per la spedizione oggi', countdown_tomorrow: 'Ordina entro {t} per la spedizione domani', or_card: 'O PAGA CON CARTA', address: 'Indirizzo di spedizione', ship_method: 'Metodo di spedizione', pay: 'Conferma il mio ordine', secured_via: 'Pagamento sicuro tramite', your_order: 'Il tuo ordine', qty: 'Quantità', total: 'Totale', to_choose: 'Da scegliere' },
  nl: { trust: ['100% veilige betaling', 'Niet goed, geld terug', 'Gratis verzending met tracking'], summary: 'Besteloverzicht', shipping: 'Verzending', free_ship: 'Gratis verzending', eta: 'Geschatte levering tussen {a} en {b}', payment: 'Betaling', secure_conn: 'Beveiligde verbinding', express: 'EXPRESS BETALEN IN 1 KLIK', countdown_today: 'Bestel binnen {t} voor verzending vandaag', countdown_tomorrow: 'Bestel binnen {t} voor verzending morgen', or_card: 'OF BETAAL MET KAART', address: 'Verzendadres', ship_method: 'Verzendmethode', pay: 'Bestelling bevestigen', secured_via: 'Veilige betaling via', your_order: 'Je bestelling', qty: 'Aantal', total: 'Totaal', to_choose: 'Te kiezen' },
};
const TRUST_ICONS = [Lock, Undo2, Truck];

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (cutoffHour) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const target = new Date(now);
  target.setHours(cutoffHour, 0, 0, 0);
  const today = target.getTime() > now;
  if (!today) target.setDate(target.getDate() + 1);
  const diff = Math.max(0, Math.floor((target.getTime() - now) / 1000));
  return { today, text: `${pad(Math.floor(diff / 3600))}:${pad(Math.floor((diff % 3600) / 60))}:${pad(diff % 60)}` };
};

const etaRange = (lang, minDays, maxDays) => {
  const fmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' });
  const a = new Date(); a.setDate(a.getDate() + minDays);
  const b = new Date(); b.setDate(b.getDate() + maxDays);
  return [fmt.format(a), fmt.format(b)];
};

const Card = ({ children, testid, className = '' }) => (
  <section data-testid={testid} className={`bg-white rounded-2xl border border-zinc-300 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${className}`}>{children}</section>
);

export const BrandCheckout = ({ vm, parts }) => {
  const {
    branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount, summaryOfferLabel, selectedShipping, total,
    offerTag, skipped, isCartMode, quantity, setQuantity, psp, vivaCfg, payzenCfg, stripeInstance, elementsOptions,
    form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions, selectedShippingId, setSelectedShippingId,
    source, stripeItems, isPreview, geoLang, paymentMode, currency, promo, promoDiscount, promoProps,
  } = vm;
  const { CustomerFields, ShippingPicker, PayForm, ExpressCheckout, StripeInvoicePay, TrustpilotBlock, money, resolveLang } = parts;

  const lang = resolveLang(branding.language, geoLang);
  const L = BL[lang] || BL.en;
  const ui = branding.checkout_ui || {};
  const brand = ui.brand || {};
  const accent = branding.accent_color || brand.accent || '#111111';
  const bg = brand.background || '#f6f5f1';
  const textColor = brand.text || '#1a1a1a';
  const font = brand.font_family ? `"${brand.font_family}", system-ui, -apple-system, "Segoe UI", sans-serif` : 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  const cutoff = Number.isFinite(+brand.cutoff_hour) ? +brand.cutoff_hour : 17;
  const [etaA, etaB] = etaRange(lang, +brand.delivery_min_days || 3, +brand.delivery_max_days || 7);
  const loc = (ui.i18n && ui.i18n[lang]) || {};
  const trust = (loc.trust_items && loc.trust_items.length >= 3 ? loc.trust_items.slice(0, 3).map((x) => x.title) : L.trust);
  const payLabel = loc.pay_button_text || L.pay;
  const showExpress = ui.show_express !== false;
  const countdown = useCountdown(cutoff);
  const [open, setOpen] = useState(false);
  const [expressAvail, setExpressAvail] = useState(false);
  const lineGross = (line) => line.product.price * line.quantity;

  useEffect(() => {
    if (!brand.font_url) return undefined;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = brand.font_url;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [brand.font_url]);

  const shipLabel = useMemo(() => {
    if (!selectedShipping) return L.to_choose;
    return selectedShipping.price === 0 ? L.free_ship : money(selectedShipping.price);
  }, [selectedShipping, L, money]);

  const Items = cartLines.map((line, idx) => {
    const img = line.variant?.image || line.product.image_url;
    const disc = lineDiscounts[idx] || 0;
    return (
      <div key={`${line.product.id}-${idx}`} className="flex gap-3 items-center" data-testid={`checkout-cart-line-${idx}`}>
        {img ? <img src={resolveImageUrl(img)} alt={line.product.name} className="w-20 h-20 object-cover rounded-xl border border-zinc-200 shrink-0" data-testid={`checkout-cart-line-${idx}-image`} /> : <div className="w-20 h-20 rounded-xl bg-zinc-100 border border-zinc-200 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px] leading-snug">{line.product.name}{line.variant?.title ? ` - ${line.variant.title}` : ''}</div>
          <div className="text-sm text-zinc-500 mt-0.5 flex items-center gap-2">
            {L.qty} : {line.quantity}
            {!isCartMode && cartLines.length === 1 && (
              <span className="inline-flex items-center gap-1 ml-1">
                <button type="button" data-testid="checkout-qty-minus" onClick={() => setQuantity(quantity - 1)} className="w-6 h-6 rounded border border-zinc-300 text-zinc-600 leading-none">−</button>
                <button type="button" data-testid="checkout-qty-plus" onClick={() => setQuantity(quantity + 1)} className="w-6 h-6 rounded border border-zinc-300 text-zinc-600 leading-none">+</button>
              </span>
            )}
          </div>
          {line.product.bogo_enabled && disc > 0 && <div className="text-[11px] font-bold uppercase text-emerald-700 mt-1" data-testid={`checkout-cart-line-${idx}-bogo`}>{offerTag(line.product)}</div>}
        </div>
        <div className="font-semibold text-[15px] shrink-0" data-testid={`checkout-cart-line-${idx}-price`}>
          {disc > 0 ? <><span className="line-through text-zinc-400 text-xs block text-right">{money(lineGross(line))}</span>{money(lineGross(line) - disc)}</> : money(lineGross(line))}
        </div>
      </div>
    );
  });

  const ShipRows = (
    <>
      <div className="flex items-center justify-between py-1">
        <span className="text-zinc-600">{L.shipping}</span>
        <span className="text-sm font-medium rounded-full border px-3 py-1" style={{ borderColor: accent, color: textColor }} data-testid="brand-shipping-pill">{shipLabel}</span>
      </div>
      <p className="text-sm text-zinc-600 flex items-center gap-2 mt-1" data-testid="brand-eta">
        <Truck className="h-4 w-4 shrink-0" /> <span>{L.eta.split('{a}')[0]}<b className="text-zinc-800">{etaA}</b>{L.eta.split('{a}')[1].split('{b}')[0]}<b className="text-zinc-800">{etaB}</b></span>
      </p>
    </>
  );

  const OrderCard = (
    <Card testid="brand-order-card" className="p-5">
      <h2 className="text-xl font-bold mb-5">{L.your_order}</h2>
      <div className="space-y-4" data-testid={CHECKOUT.productCard}>{Items}</div>
      <div className="border-t border-zinc-200 mt-5 pt-4 space-y-1">
        {ShipRows}
        {bogoDiscount > 0 && <div className="flex justify-between text-emerald-700 text-sm pt-2" data-testid="checkout-bogo-discount"><span>{summaryOfferLabel}</span><span>−{money(bogoDiscount)}</span></div>}
        <div className="text-sm pt-1"><PromoSummaryRow promo={promo} discount={promoDiscount} lang={lang} money={money} /></div>
        <div className="flex items-center justify-between pt-3">
          <span className="text-xl font-bold">{L.total}</span>
          <span className="text-xl font-bold" data-testid={CHECKOUT.totalAmount}>{money(total)}</span>
        </div>
        <div className="pt-3"><PromoCodeField {...promoProps} accent={accent} tidSuffix="-brand" /></div>
      </div>
      {skipped.length > 0 && <div data-testid="checkout-skipped-warning" className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">⚠️ {skipped.length > 1 ? t.skipped_many.replace('{n}', skipped.length) : t.skipped_one}</div>}
      <TrustpilotBlock ui={ui} className="mt-5" />
    </Card>
  );

  const payProps = {
    items: stripeItems, total, form, hasRequiredCustomerInfo, source, shippingOptionId: selectedShippingId,
    shippingChosen: !!selectedShipping, accentColor: accent, storeId: pixels.store_id, storeSlug: pixels.store_slug, t,
  };

  return (
    <div className="min-h-screen pb-10" style={{ backgroundColor: bg, color: textColor, fontFamily: font }} data-testid="brand-checkout-form">
      {pixels.store_id && !isPreview && (
        <PixelTracker storeId={pixels.store_id} slug={pixels.store_slug} event="page_view_checkout" metaPixelId={pixels.meta_pixel_id} tiktokPixelId={pixels.tiktok_pixel_id} googleTagId={pixels.google_tag_id} googleLabel={pixels.google_conversion_label} />
      )}
      <header className="flex items-center justify-center pt-6 pb-5 px-4" data-testid="brand-store-brand">
        {branding.logo_url ? (
          <img src={resolveImageUrl(branding.logo_url)} alt={branding.display_name} className="h-[88px] max-w-[240px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <span className="text-2xl font-extrabold tracking-tight">{branding.display_name || t.store_fallback}</span>
        )}
      </header>

      <div className="max-w-[1080px] mx-auto px-3 sm:px-6 lg:grid lg:grid-cols-[1fr_400px] lg:gap-6 lg:items-start">
        <div className="space-y-4">
          <Card testid="brand-trust-card" className="p-4 sm:p-5">
            <div className="grid grid-cols-3 gap-2 pb-4 border-b border-zinc-200">
              {trust.map((label, i) => { const Ic = TRUST_ICONS[i]; return (
                <div key={i} className="flex flex-col items-center text-center gap-2" data-testid={`brand-trust-${i}`}>
                  <Ic className="h-6 w-6" style={{ color: accent }} strokeWidth={1.8} />
                  <span className="text-[13px] leading-tight font-medium text-zinc-700">{label}</span>
                </div>
              ); })}
            </div>
            <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between py-4" data-testid="brand-summary-toggle">
              <span className="text-[17px] font-bold text-left">{L.summary}</span>
              <span className="flex items-center gap-2 text-[17px] font-bold whitespace-nowrap shrink-0">{money(total)}{open ? <ChevronUp className="h-5 w-5 text-zinc-500" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}</span>
            </button>
            {open && <div className="space-y-4 pb-4" data-testid="brand-summary-items">{Items}</div>}
            <div className="border-t border-zinc-200 pt-3">{ShipRows}</div>
          </Card>

          <Card testid="brand-payment-card" className="p-4 sm:p-5">
            <div className="flex items-center justify-between pb-4 border-b border-zinc-200">
              <h2 className="text-2xl font-bold">{L.payment}</h2>
              <span className="text-sm text-zinc-600 flex items-center gap-1.5"><Lock className="h-4 w-4" /> {L.secure_conn}</span>
            </div>

            {showExpress && psp === 'stripe' && paymentMode === 'element' && stripeInstance && elementsOptions && (
              <div className={expressAvail ? 'pt-4' : ''} data-testid="brand-express-zone">
                {expressAvail && <p className="text-center text-[12px] font-semibold tracking-[0.12em] text-zinc-500 mb-3">{L.express}</p>}
                <Elements stripe={stripeInstance} options={elementsOptions}>
                  <ExpressCheckout {...payProps} amountCents={Math.max(50, Math.round(total * 100))} selectedShipping={selectedShipping} hideDivider onAvailable={setExpressAvail} />
                </Elements>
                {expressAvail && <div className="border-t border-dashed border-zinc-300 -mt-2" />}
              </div>
            )}

            {brand.show_countdown !== false && (
              <div className="mt-4 rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3 text-[15px] flex items-start gap-2.5" data-testid="brand-countdown">
                <Clock className="h-5 w-5 mt-0.5 shrink-0 text-zinc-600" />
                <span>
                  {(countdown.today ? L.countdown_today : L.countdown_tomorrow).split('{t}')[0]}
                  <b className="font-bold tabular-nums" data-testid="brand-countdown-timer">{countdown.text}</b>
                  {(countdown.today ? L.countdown_today : L.countdown_tomorrow).split('{t}')[1]}
                </span>
              </div>
            )}

            {expressAvail ? (
              <div className="flex items-center gap-3 my-5 text-[12px] font-semibold tracking-[0.12em] text-zinc-500" data-testid="brand-or-card">
                <span className="flex-1 h-px bg-zinc-300" />{L.or_card}<span className="flex-1 h-px bg-zinc-300" />
              </div>
            ) : <div className="my-5" />}

            <div className="space-y-4 [&_label]:text-[15px] [&_label]:text-zinc-800 [&_label]:font-medium [&_input]:border-zinc-300 [&_input]:bg-white [&_input]:rounded-lg">
              <CustomerFields form={form} updateField={updateField} setForm={setForm} t={t} />
            </div>

            <div className="mt-6" data-testid="brand-shipping-section">
              <div className="text-[15px] font-medium text-zinc-800 mb-2">{L.ship_method}</div>
              <ShippingPicker options={shippingOptions} value={selectedShippingId} onChange={setSelectedShippingId} accentColor={accent} t={t} />
            </div>

            <div className="mt-6" data-testid="brand-pay-section">
              {psp === 'payzen' ? (
                <PayzenPayForm {...payProps} currency={currency} payzenCfg={payzenCfg} />
              ) : psp === 'viva' ? (
                vivaCfg ? <VivaPayForm {...payProps} vivaCfg={vivaCfg} firstProductId={cartLines[0]?.product?.id} currency="eur" embedded={false} />
                  : <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading-viva"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
              ) : (paymentMode === 'invoice' || paymentMode === 'link') ? (
                <StripeInvoicePay mode={paymentMode} {...payProps} payLabel={payLabel} brand />
              ) : stripeInstance && elementsOptions ? (
                <Elements stripe={stripeInstance} options={elementsOptions}>
                  <PayForm {...payProps} amountCents={Math.max(50, Math.round(total * 100))} payLabel={payLabel} brand />
                </Elements>
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
              )}
              {psp === 'stripe' && <p className="mt-4 text-center text-sm text-zinc-500 flex items-center justify-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {L.secured_via} <span className="font-semibold" style={{ color: '#635bff' }}>Stripe</span></p>}
            </div>
          </Card>

        </div>

        <div className="mt-4 lg:mt-0 lg:sticky lg:top-6">{OrderCard}</div>
      </div>
    </div>
  );
};
